import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useRouter } from 'next/router'

import { useAuthentication } from '@hooks/useAuthentication'
import { TimeRangeEnum, timeRangeValue } from '@interfaces/TimeRangeEnum'
import { BookEnum } from '@interfaces/BookEnum'
import { WriterSalesAnalyticsOverviewDataType } from '@models/myWriting/salesAndAnalytics/WriterSalesAnalyticsOverviewDataType'
import { useMyWritingAction } from '@hooks/book/myWriting/useMyWritingAction'
import { WriterSalesAnalyticsDetailReportEnum } from '@interfaces/WriterSalesAnalyticsDetailReportEnum'
import { SaleAndAnalyticsQueryParams } from './interfaces/SaleAndAnalyticsQueryParams'
import { SearchSaleAnalyticsType } from './components/SearchSaleAnalytics/interfaces/SearchSaleAnalyticsType'
import { SaleAnalyticsTabProps } from './interfaces'

const initialData: WriterSalesAnalyticsOverviewDataType = {
  bookTitle: '',
  bookType: '',
  sales: 0,
  donation: 0,
  viewCount: 0,
  likeCount: 0,
  shelfAddedCount: 0,
  commentCount: 0,
  genderDetail: [
    {
      name: 'ชาย',
      value: 0,
    },
    {
      name: 'หญิง',
      value: 0,
    },
    {
      name: 'ไม่ระบุเพศ',
      value: 0,
    },
  ],
  ageDetail: [
    {
      name: 'ต่ำกว่า 15',
      value: 0,
    },
    {
      name: '16-20',
      value: 0,
    },
    {
      name: '21-30',
      value: 0,
    },
    {
      name: '31-40',
      value: 0,
    },
    {
      name: '41 ขึ้นไป',
      value: 0,
    },
    {
      name: 'ไม่ระบุ',
      value: 0,
    },
  ],
}

const currentTime = DateTime.now()

export const withSaleAnalyticsTab = (
  Component: React.FC<SaleAnalyticsTabProps>
) => {
  function WithSaleAnalyticsTab() {
    const myWritingClient = useMyWritingAction()
    const router = useRouter()
    const advanceSearch = router.query.advanceSearch === 'true'
    const subTab = router.query.subtab || 'overview'
    const { user, isAuthenticated, isWriter } = useAuthentication()
    const [queryParams, setQueryParams] = useState<SaleAndAnalyticsQueryParams>(
      {
        bookId: undefined,
        bookTypes: undefined,
        categoryIds: undefined,
        penNameIds: undefined,
        startDate: currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.MONTH] })
          .startOf('day'),
        endDate: currentTime.endOf('day'),
      }
    )
    const queryKey = ['sales-analytics-overview', user.id, queryParams]
    const { data = initialData, isLoading } = useQuery(
      queryKey,
      () => myWritingClient.getWriterSalesAnalyticsOverview(queryParams),
      {
        enabled: !!isAuthenticated && isWriter && subTab === 'overview',
      }
    )

    const handleSearch = async ({
      bookId,
      categoryIds,
      penNameId,
      bookType,
      startAt,
      endAt,
      timeRange,
    }: SearchSaleAnalyticsType) => {
      if (!timeRange && (!startAt || !endAt)) return

      const query: any = { tab: 'salesAnalytics', advanceSearch }
      if (timeRange) query.timeRange = timeRange
      if (startAt) query.startDate = startAt.toString()
      if (endAt) query.endDate = endAt.toString()
      if (bookId) {
        let reportType = WriterSalesAnalyticsDetailReportEnum.SALES_BY_CHAPTER
        if (
          router.query?.reportType &&
          [
            WriterSalesAnalyticsDetailReportEnum.SALES_BY_CHAPTER,
            WriterSalesAnalyticsDetailReportEnum.DONATION,
            WriterSalesAnalyticsDetailReportEnum.VIEW,
            WriterSalesAnalyticsDetailReportEnum.SALES_BY_BOOK,
          ].includes(
            router.query.reportType as WriterSalesAnalyticsDetailReportEnum
          )
        ) {
          reportType = router.query
            .reportType as WriterSalesAnalyticsDetailReportEnum
        }

        query.bookId = bookId
        query.reportType = reportType
      }
      if (categoryIds.length) query.categoryIds = categoryIds
      if (penNameId.length) query.penNameIds = penNameId
      if (bookType.length) query.bookTypes = bookType

      router.replace(
        {
          pathname: '/my-writing',
          query,
        },
        undefined,
        { scroll: false }
      )
    }

    useEffect(() => {
      let newStartAt = router.query.startDate
        ? DateTime.fromJSDate(new Date(router.query.startDate as string))
        : currentTime
            .minus({ days: timeRangeValue[TimeRangeEnum.MONTH] })
            .startOf('day')
      let newEndAt = router.query.endDate
        ? DateTime.fromJSDate(new Date(router.query.endDate as string)).endOf(
            'day'
          )
        : currentTime.endOf('day')

      if (router.query.timeRange === TimeRangeEnum.DAY) {
        newStartAt = currentTime.startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.WEEK) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.WEEK] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.MONTH) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.MONTH] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.THREE_MONTH) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.THREE_MONTH] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.SIX_MONTH) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.SIX_MONTH] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      } else if (router.query.timeRange === TimeRangeEnum.YEAR) {
        newStartAt = currentTime
          .minus({ days: timeRangeValue[TimeRangeEnum.YEAR] })
          .startOf('day')
        newEndAt = currentTime.endOf('day')
      }

      const bookTypes = router.query.bookTypes
        ? typeof router.query.bookTypes === 'string'
          ? ([router.query.bookTypes] as BookEnum[])
          : (router.query.bookTypes as BookEnum[])
        : undefined

      const penNameIds = router.query.penNameIds
        ? typeof router.query.penNameIds === 'string'
          ? [router.query.penNameIds]
          : router.query.penNameIds
        : undefined

      const categoryIds = router.query.categoryIds
        ? typeof router.query.categoryIds === 'string'
          ? [router.query.categoryIds]
          : router.query.categoryIds
        : undefined

      if (newStartAt && newEndAt) {
        setQueryParams({
          bookId: Number(router.query.bookId) || undefined,
          categoryIds,
          penNameIds,
          bookTypes,
          startDate: newStartAt,
          endDate: newEndAt,
        })
      }
    }, [
      router.query.bookId,
      router.query.bookTypes,
      router.query.categoryIds,
      router.query.penNameIds,
      router.query.startDate,
      router.query.endDate,
      router.query.timeRange,
    ])

    function handleTabChange(value: string) {
      router.replace({
        pathname: '/my-writing',
        query: {
          tab: router.query.tab,
          subtab: value,
        },
      })
    }

    const newProps = {
      initialValues: {
        bookId: queryParams.bookId,
        bookType: queryParams.bookTypes || [],
        categoryIds: queryParams.categoryIds || [],
        penNameId: queryParams.penNameIds || [],
        timeRange:
          router.query.startDate && router.query.endDate
            ? undefined
            : (router.query.timeRange as TimeRangeEnum) || TimeRangeEnum.MONTH,
        startAt: router.query.startDate
          ? queryParams.startDate.toJSDate()
          : undefined,
        endAt: router.query.endDate
          ? queryParams.endDate.toJSDate()
          : undefined,
      },
      data,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate,
      queryParams,
      handleSearch,
      handleTabChange,
    }
    return <Component {...newProps} />
  }
  return WithSaleAnalyticsTab
}
